import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "../../../firebase";
import {
  DatasetSummary,
  DebateResult,
  MonthlyCurationRecord,
  SaveMonthlyCurationResult,
} from "./types";

const buildMonthlyCurationPath = (affiliation: string) =>
  `affiliations/${affiliation}/monthlyCurations`;

const buildMonthlyCurationRef = (affiliation: string, monthKey: string) =>
  doc(db, `${buildMonthlyCurationPath(affiliation)}/${monthKey}`);

const parseMonthKey = (monthKey: string) => {
  const [yearText, monthText] = String(monthKey || "").split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  return {
    year: Number.isFinite(year) ? year : 0,
    month: Number.isFinite(month) ? month : 0,
  };
};

const sanitizeDatasetSummaryForStorage = (
  datasetSummary: DatasetSummary,
): DatasetSummary => ({
  ...datasetSummary,
  top_products: Array.isArray(datasetSummary.top_products)
    ? datasetSummary.top_products.slice(0, 80)
    : [],
  requested_keywords: Array.isArray(datasetSummary.requested_keywords)
    ? datasetSummary.requested_keywords.slice(0, 20)
    : [],
  high_margin_products: [],
  coupang_references: Array.isArray(datasetSummary.coupang_references)
    ? datasetSummary.coupang_references.slice(0, 80).map((reference) => ({
        ...reference,
        price_samples: [],
      }))
    : [],
});

const parseMonthlyCurationRecord = (
  id: string,
  raw: any,
): MonthlyCurationRecord => ({
  id,
  affiliation: String(raw?.affiliation || ""),
  month_key: String(raw?.month_key || id),
  year: Number(raw?.year || 0),
  month: Number(raw?.month || 0),
  requirement: String(raw?.requirement || ""),
  customer_facing: Boolean(raw?.customer_facing),
  dataset_summary: raw?.dataset_summary as DatasetSummary,
  debate_result: raw?.debate_result as DebateResult,
  item_count: Number(raw?.item_count || 0),
  estimated_total_cost: Number(raw?.estimated_total_cost || 0),
  created_at: raw?.created_at,
  updated_at: raw?.updated_at,
});

export const getMonthlyCuration = async (
  affiliation: string,
  monthKey: string,
): Promise<MonthlyCurationRecord | null> => {
  if (!affiliation || !monthKey) return null;
  const snap = await getDoc(buildMonthlyCurationRef(affiliation, monthKey));
  if (!snap.exists()) return null;
  return parseMonthlyCurationRecord(snap.id, snap.data());
};

export const listMonthlyCurations = async (
  affiliation: string,
): Promise<MonthlyCurationRecord[]> => {
  if (!affiliation) return [];
  const snap = await getDocs(collection(db, buildMonthlyCurationPath(affiliation)));
  return snap.docs
    .map((docSnap) => parseMonthlyCurationRecord(docSnap.id, docSnap.data()))
    .sort((a, b) => b.month_key.localeCompare(a.month_key));
};

export const saveMonthlyCuration = async ({
  affiliation,
  monthKey,
  requirement,
  datasetSummary,
  debateResult,
  customerFacing,
}: {
  affiliation: string;
  monthKey: string;
  requirement: string;
  datasetSummary: DatasetSummary;
  debateResult: DebateResult;
  customerFacing: boolean;
}): Promise<SaveMonthlyCurationResult> => {
  if (!affiliation) {
    throw new Error("저장할 고객사 정보가 없습니다.");
  }
  if (!monthKey) {
    throw new Error("저장할 월 정보가 없습니다.");
  }

  const ref = buildMonthlyCurationRef(affiliation, monthKey);
  const existingSnap = await getDoc(ref);
  const existingData = existingSnap.exists() ? existingSnap.data() : null;
  const { year, month } = parseMonthKey(monthKey);

  await setDoc(ref, {
    affiliation,
    month_key: monthKey,
    year,
    month,
    requirement: String(requirement || "").trim(),
    customer_facing: customerFacing,
    dataset_summary: sanitizeDatasetSummaryForStorage(datasetSummary),
    debate_result: debateResult,
    item_count: debateResult.finalConsensus.items.length,
    estimated_total_cost: debateResult.finalConsensus.estimated_total_cost,
    created_at: existingData?.created_at || serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  const savedSnap = await getDoc(ref);
  if (!savedSnap.exists()) {
    throw new Error("큐레이션 결과 저장 후 다시 읽어오지 못했습니다.");
  }

  return {
    existed: existingSnap.exists(),
    record: parseMonthlyCurationRecord(savedSnap.id, savedSnap.data()),
  };
};
